import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { useI18n } from "@/lib/i18n";

type SearchItem = {
  label: string;
  hint?: string;
  keywords: string;
  to: string; // pathname (+ optional hash)
};

interface SearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SearchDialog = ({ open, onOpenChange }: SearchDialogProps) => {
  const { lang } = useI18n();
  const navigate = useNavigate();
  const isEn = lang === "en";
  const root = isEn ? "/en" : "/";

  const sections: SearchItem[] = useMemo(
    () => [
      {
        label: isEn ? "Why A-WAVE" : "Waarom A-WAVE",
        hint: isEn ? "The problem we solve" : "Het probleem dat we oplossen",
        keywords: "why waarom problem probleem visibility zichtbaarheid leads",
        to: `${root}#why`,
      },
      {
        label: isEn ? "Who we are" : "Wie we zijn",
        hint: isEn ? "Team: Ellen, Ron, Larissa" : "Team: Ellen, Ron, Larissa",
        keywords: "team founder ellen ron larissa about over ons",
        to: `${root}#who`,
      },
      {
        label: isEn ? "What we do" : "Wat we doen",
        hint: "Audit · Accelerate · Activate · Assess",
        keywords:
          "services diensten audit accelerate activate assess seo sea ads marketing content website",
        to: `${root}#what`,
      },
      {
        label: isEn ? "For whom" : "Voor wie",
        hint: isEn ? "Do you recognise this?" : "Jij herkent dit",
        keywords: "voor wie for whom recognise herkent industrieel technisch product",
        to: `${root}#what`,
      },
      {
        label: isEn ? "References" : "Referenties",
        hint: "Kruidvat · VodafoneZiggo · ICI PARIS XL · gasfedershop.de",
        keywords:
          "references referenties campagnes campaigns clients kruidvat vodafone ici paris gasfedershop tedx",
        to: `${root}#refs`,
      },
      {
        label: isEn ? "Partners & Network" : "Partners & Netwerk",
        hint: "FESMA · SmartIQ · Power Networking",
        keywords: "partners netwerk network fesma smartiq larissa events",
        to: `${root}#refs`,
      },
      {
        label: isEn ? "Ride the A-WAVE" : "Plan kennismaking",
        hint: isEn ? "Book a 30-min intro" : "30 minuten, gratis",
        keywords: "contact intake kennismaking whatsapp ride wave cta",
        to: `${root}#what`,
      },
    ],
    [isEn, root]
  );

  const pages: SearchItem[] = useMemo(
    () => [
      {
        label: isEn ? "Marketing Glossary" : "Marketing Woordenlijst",
        hint: isEn ? "Definitions for technical professionals" : "Begrippen voor techneuten",
        keywords: "glossary woordenlijst begrippen termen definitions seo sea marketing",
        to: isEn ? "/glossary" : "/woordenlijst",
      },
      {
        label: "FuelFixer Intake",
        hint: isEn ? "Structured intake survey" : "Intake-vragenlijst",
        keywords: "fuelfixer survey intake questionnaire vragenlijst",
        to: "/survey-fuelfixer",
      },
    ],
    [isEn]
  );

  // ⌘K / Ctrl+K shortcut
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  const go = (to: string) => {
    onOpenChange(false);
    const [path, hash] = to.split("#");
    navigate(path + (hash ? `#${hash}` : ""));
    if (hash) {
      // give the route a tick to mount, then scroll
      setTimeout(() => {
        const el = document.getElementById(hash);
        el?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder={isEn ? "Search the site…" : "Zoek op de site…"}
      />
      <CommandList>
        <CommandEmpty>{isEn ? "No results." : "Geen resultaten."}</CommandEmpty>
        <CommandGroup heading={isEn ? "Sections" : "Secties"}>
          {sections.map((item) => (
            <CommandItem
              key={item.to + item.label}
              value={`${item.label} ${item.hint ?? ""} ${item.keywords}`}
              onSelect={() => go(item.to)}
            >
              <div className="flex flex-col">
                <span className="text-sm">{item.label}</span>
                {item.hint && (
                  <span className="text-xs text-muted-foreground">{item.hint}</span>
                )}
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        <CommandGroup heading={isEn ? "Pages" : "Pagina's"}>
          {pages.map((item) => (
            <CommandItem
              key={item.to}
              value={`${item.label} ${item.hint ?? ""} ${item.keywords}`}
              onSelect={() => go(item.to)}
            >
              <div className="flex flex-col">
                <span className="text-sm">{item.label}</span>
                {item.hint && (
                  <span className="text-xs text-muted-foreground">{item.hint}</span>
                )}
              </div>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default SearchDialog;
